import { Box, Typography } from "@mui/material";
import { formatCPF, formatCep } from "../../utils/formats";

const formatMoney = (value) => {
	return Number(value || 0).toLocaleString("pt-BR", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

function hasDecimalPlaces(value) {
	return Number(value) % 1 !== 0;
}

function getDecimalPart(value) {
	const roundedValue = Number(value || 0).toFixed(2);
	const decimalStr = roundedValue.split(".")[1];

	return decimalStr ? parseInt(decimalStr, 10) : 0;
}

function numberToPortugueseText(value) {
	value = Number(value || 0);

	const units = [
		"",
		"um",
		"dois",
		"três",
		"quatro",
		"cinco",
		"seis",
		"sete",
		"oito",
		"nove",
	];

	const teens = [
		"dez",
		"onze",
		"doze",
		"treze",
		"catorze",
		"quinze",
		"dezesseis",
		"dezessete",
		"dezoito",
		"dezenove",
	];

	const tens = [
		"",
		"",
		"vinte",
		"trinta",
		"quarenta",
		"cinquenta",
		"sessenta",
		"setenta",
		"oitenta",
		"noventa",
	];

	const hundreds = [
		"",
		"cento",
		"duzentos",
		"trezentos",
		"quatrocentos",
		"quinhentos",
		"seiscentos",
		"setecentos",
		"oitocentos",
		"novecentos",
	];

	if (value === 0) {
		return "ZERO REAIS";
	}

	const originalValue = Number(value).toFixed(2);

	let inteiro = Math.floor(value);
	let result = "";
	let decimals = "";

	if (inteiro >= 1000) {
		const thousandsPart = Math.floor(inteiro / 1000);

		result +=
			thousandsPart > 1
				? `${units[thousandsPart]} mil`
				: "mil";

		inteiro %= 1000;
	}

	if (inteiro >= 100) {
		const hundredsPart = Math.floor(inteiro / 100);

		result +=
			(result ? ", " : "") +
			(hundredsPart === 1 && inteiro % 100 === 0
				? "cem"
				: hundreds[hundredsPart]);

		inteiro %= 100;
	}

	if (inteiro >= 20) {
		const tensPart = Math.floor(inteiro / 10);

		result +=
			(result ? " e " : "") +
			tens[tensPart];

		inteiro %= 10;
	}

	if (inteiro >= 10) {
		result +=
			(result ? " e " : "") +
			teens[inteiro - 10];

		inteiro = 0;
	}

	if (inteiro > 0) {
		result +=
			(result ? " e " : "") +
			units[inteiro];
	}

	if (hasDecimalPlaces(Number(originalValue))) {
		let decimalValue = getDecimalPart(
			Number(originalValue)
		);

		if (decimalValue >= 20) {
			const tensPart = Math.floor(
				decimalValue / 10
			);

			decimals +=
				(decimals ? " e " : "") +
				tens[tensPart];

			decimalValue %= 10;
		}

		if (decimalValue >= 10) {
			decimals +=
				(decimals ? " e " : "") +
				teens[decimalValue - 10];

			decimalValue = 0;
		}

		if (decimalValue > 0) {
			decimals +=
				(decimals ? " e " : "") +
				units[decimalValue];
		}
	}

	result =
		result.charAt(0).toUpperCase() +
		result.slice(1) +
		" REAIS";

	if (decimals) {
		result += ` E ${decimals.toUpperCase()} CENTAVOS`;
	}

	return result.toUpperCase();
}

function dateToPortugueseText(date) {
	if (!date) {
		return "-";
	}

	const months = [
		"janeiro",
		"fevereiro",
		"março",
		"abril",
		"maio",
		"junho",
		"julho",
		"agosto",
		"setembro",
		"outubro",
		"novembro",
		"dezembro",
	];

	const [year, month, day] = String(date)
		.slice(0, 10)
		.split("-")
		.map(Number);

	if (!year || !month || !day) {
		return "-";
	}

	return `${day} de ${months[month - 1]} de ${year} `
		.toUpperCase();
}

const formatDate = (date) => {
	if (!date) {
		return "-";
	}

	const [year, month, day] = String(date)
		.slice(0, 10)
		.split("-");

	if (!year || !month || !day) {
		return "-";
	}

	return `${day} /${month}/${year} `;
};

const formatCreatedAt = (date) => {
	if (!date) {
		return "-";
	}

	const parsedDate = new Date(date);

	if (Number.isNaN(parsedDate.getTime())) {
		return "-";
	}

	return parsedDate.toLocaleDateString("pt-BR");
};

const getKitTotal = (kit) => {
	if (!Array.isArray(kit?.kit_products)) {
		return 0;
	}

	return kit.kit_products.reduce(
		(accumulator, product) => {
			const quantity = Number(
				product?.quantity || 0
			);

			const sellPrice = Number(
				product?.sell_price || 0
			);

			return (
				accumulator +
				quantity * sellPrice
			);
		},
		0
	);
};

const ContractPromissoryPage = ({
	sellerData,
	selectedKit = null,
	standalonePromissory = null,
}) => {
	const isStandalonePromissory = Boolean(
		standalonePromissory
	);

	const hasPromissoryData =
		Boolean(selectedKit) ||
		isStandalonePromissory;

	if (!sellerData || !hasPromissoryData) {
		return null;
	}

	const valor = isStandalonePromissory
		? Number(
			standalonePromissory?.value || 0
		)
		: getKitTotal(selectedKit);

	const payDate = isStandalonePromissory
		? standalonePromissory?.dueDate || ""
		: selectedKit?.pay_date ||
		selectedKit?.data?.pay_date ||
		"";

	const createdAt = isStandalonePromissory
		? new Date().toISOString()
		: selectedKit?.created_at ||
		selectedKit?.data?.created_at ||
		"";

	const createdAtFormatted =
		formatCreatedAt(createdAt);

	const kitNumber = isStandalonePromissory
		? null
		: selectedKit?.kit_number || null;

	const enderecoCompleto = [
		sellerData?.end_logradouro,
		sellerData?.end_numero,
		sellerData?.end_complemento,
		sellerData?.end_bairro,
		sellerData?.end_cep
			? formatCep(
				Number(
					sellerData.end_cep
				)
			)
			: "",
		sellerData?.end_municipio,
	]
		.filter(Boolean)
		.join(", ");

	return (
		<Box
			className="promissory-print-page"
			sx={{
				width: "100%",
				minHeight: "1122px",
				backgroundColor: "#fff",
				color: "#000",
				padding: "48px 56px",
				boxSizing: "border-box",
				pageBreakBefore: "always",
				breakBefore: "page",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent:
						"space-between",
					alignItems: "center",
					mb: 4,
				}}
			>
				<Typography
					sx={{
						fontSize: "28px",
						fontWeight: 800,
					}}
				>
					NOTA PROMISSÓRIA
				</Typography>

				<Typography
					sx={{
						fontSize: "20px",
						fontWeight: 700,
					}}
				>
					01/01
				</Typography>
			</Box>

			{kitNumber ? (
				<Typography
					sx={{
						fontSize: "15px",
						fontWeight: 700,
						mb: 2,
					}}
				>
					REFERENTE AO KIT Nº{" "}
					{kitNumber}
				</Typography>
			) : (
				<Typography
					sx={{
						fontSize: "15px",
						fontWeight: 700,
						mb: 2,
					}}
				>
					NOTA PROMISSÓRIA AVULSA
				</Typography>
			)}

			<Box
				sx={{
					display: "flex",
					justifyContent:
						"space-between",
					alignItems: "flex-end",
					mb: 3,
				}}
			>
				<Typography
					sx={{ fontSize: "18px" }}
				>
					Vencimento:{" "}
					<b>{formatDate(payDate)}</b>
				</Typography>

				<Typography
					sx={{
						fontSize: "30px",
						fontWeight: 800,
					}}
				>
					R$ {formatMoney(valor)}
				</Typography>
			</Box>

			<Box
				sx={{
					borderTop:
						"2px solid #000",
					pt: 3,
					mb: 3,
				}}
			>
				<Typography
					sx={{
						fontSize: "18px",
						lineHeight: 1.8,
					}}
				>
					No dia{" "}
					<b>
						{dateToPortugueseText(
							payDate
						)}
					</b>
					, pagarei por esta única via
					de NOTA PROMISSÓRIA a{" "}
					<b>PITAYA JOIAS</b>, CNPJ{" "}
					<b>
						30.822.328/0001-54
					</b>
					, ou à sua ordem, a quantia
					de{" "}
					<b>
						{numberToPortugueseText(
							valor
						)}
					</b>{" "}
					em moeda corrente deste
					país.
				</Typography>
			</Box>

			<Box
				sx={{
					borderTop:
						"1px solid #000",
					pt: 3,
					display: "grid",
					gap: 1.5,
				}}
			>
				<Typography
					sx={{ fontSize: "17px" }}
				>
					<strong>
						Local de pagamento:
					</strong>{" "}
					Porto Alegre - RS
				</Typography>

				<Typography
					sx={{ fontSize: "17px" }}
				>
					<strong>
						Data da emissão:
					</strong>{" "}
					{createdAtFormatted}
				</Typography>

				<Typography
					sx={{ fontSize: "17px" }}
				>
					<strong>
						Nome do emitente:
					</strong>{" "}
					{sellerData?.nome || "-"}
				</Typography>

				<Typography
					sx={{ fontSize: "17px" }}
				>
					<strong>CPF:</strong>{" "}
					{sellerData?.cpf
						? formatCPF(
							sellerData.cpf
						)
						: "-"}
				</Typography>

				<Typography
					sx={{ fontSize: "17px" }}
				>
					<strong>Endereço:</strong>{" "}
					{enderecoCompleto || "-"}
				</Typography>
			</Box>

			<Box
				sx={{
					mt: 14,
					textAlign: "center",
				}}
			>
				<Typography
					sx={{
						fontSize: "18px",
						letterSpacing: 1,
					}}
				>
					_______________________________________________________
				</Typography>

				<Typography
					sx={{
						fontSize: "17px",
						fontWeight: 700,
						mt: 1,
					}}
				>
					Assinatura do emitente
				</Typography>
			</Box>
		</Box>
	);
};

export default ContractPromissoryPage;

